import { useState } from 'react';
import {
  QueueListIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  QUEUE_LABELS,
  STATUS_COLORS,
  getQueueHealthColor,
} from '../constants';

/**
 * Queue management panel with DLQ workflow.
 */
export default function QueueManager({
  queues = [],
  dlqJobs = [],
  onViewJobs,
  onFlushQueue,
  onRetryDLQ,
  onRetryAllDLQ,
}) {
  const [expandedQueue, setExpandedQueue] = useState(null);
  const [showFlushConfirm, setShowFlushConfirm] = useState(null);
  const [showRetryAllConfirm, setShowRetryAllConfirm] = useState(false);

  const handleFlush = (queueName) => {
    if (showFlushConfirm === queueName) {
      onFlushQueue && onFlushQueue(queueName);
      setShowFlushConfirm(null);
    } else {
      setShowFlushConfirm(queueName);
    }
  };

  const handleRetryAll = () => {
    if (showRetryAllConfirm) {
      onRetryAllDLQ && onRetryAllDLQ();
      setShowRetryAllConfirm(false);
    } else {
      setShowRetryAllConfirm(true);
    }
  };

  // Format age in human-readable form
  const formatAge = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    return `${Math.floor(seconds / 3600)}h`;
  };

  return (
    <div className="queue-manager">
      <div className="queue-manager-header">
        <h4>Queue Management</h4>
      </div>

      {/* Queue table */}
      <div className="queue-table-container">
        <table className="queue-table" role="grid">
          <thead>
            <tr>
              <th scope="col">Queue</th>
              <th scope="col">Depth</th>
              <th scope="col">Rate</th>
              <th scope="col">Oldest</th>
              <th scope="col">Health</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {queues.map((queue) => {
              const label = QUEUE_LABELS[queue.name] || { label: queue.name, desc: '' };
              const healthColor = getQueueHealthColor(queue.depth, queue.oldest_age_seconds);
              const isExpanded = expandedQueue === queue.name;

              return (
                <tr key={queue.name} className={queue.health === 'critical' ? 'queue-critical' : ''}>
                  <td>
                    <div className="queue-name-cell">
                      <button
                        className="expand-btn"
                        onClick={() => setExpandedQueue(isExpanded ? null : queue.name)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? <ChevronDownIcon className="icon-sm" /> : <ChevronRightIcon className="icon-sm" />}
                      </button>
                      <span className="queue-name">{label.label}</span>
                    </div>
                  </td>
                  <td>
                    <span
                      className="queue-depth"
                      style={{ color: queue.depth > 50 ? STATUS_COLORS.warning : 'inherit' }}
                    >
                      {queue.depth}
                    </span>
                  </td>
                  <td>{queue.rate_per_min}/min</td>
                  <td>{formatAge(queue.oldest_age_seconds)}</td>
                  <td>
                    <span
                      className="health-badge"
                      style={{ '--health-color': healthColor }}
                    >
                      {queue.health?.toUpperCase() || 'OK'}
                    </span>
                  </td>
                  <td className="queue-actions">
                    <button
                      className="btn-icon"
                      onClick={() => onViewJobs && onViewJobs(queue.name)}
                      title="View Jobs"
                    >
                      <QueueListIcon className="icon-sm" />
                    </button>
                    {queue.depth > 0 && (
                      <button
                        className={`btn-icon btn-danger ${showFlushConfirm === queue.name ? 'confirm' : ''}`}
                        onClick={() => handleFlush(queue.name)}
                        title={showFlushConfirm === queue.name ? 'Click again to confirm' : 'Flush Queue'}
                      >
                        <TrashIcon className="icon-sm" />
                        {showFlushConfirm === queue.name && <span className="confirm-text">Confirm?</span>}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Dead Letter Queue Section */}
      {dlqJobs && dlqJobs.length > 0 && (
        <div className="dlq-section">
          <div className="dlq-header">
            <ExclamationTriangleIcon className="icon dlq-icon" />
            <h4>Dead Letter Queue ({dlqJobs.length} jobs)</h4>
            <button
              className={`btn-retry-all ${showRetryAllConfirm ? 'confirm' : ''}`}
              onClick={handleRetryAll}
            >
              <ArrowPathIcon className="icon-sm" />
              {showRetryAllConfirm ? 'Confirm Retry All?' : 'Retry All'}
            </button>
          </div>

          <div className="dlq-list">
            {dlqJobs.slice(0, 10).map((job, idx) => (
              <div key={idx} className="dlq-job">
                <div className="dlq-job-info">
                  <span className="job-id">{job.image_id || `Job ${idx}`}</span>
                  <span className="job-error">{job.error || 'Unknown error'}</span>
                  <span className="job-age">{job.failed_at ? formatAge(Math.floor((Date.now() - new Date(job.failed_at).getTime()) / 1000)) + ' ago' : '-'}</span>
                </div>
                <div className="dlq-job-actions">
                  <button
                    className="btn-icon btn-retry"
                    onClick={() => onRetryDLQ && onRetryDLQ(idx)}
                    title="Retry"
                  >
                    <ArrowPathIcon className="icon-sm" />
                  </button>
                </div>
              </div>
            ))}
            {dlqJobs.length > 10 && (
              <div className="dlq-more">
                +{dlqJobs.length - 10} more jobs
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
